import { useState } from "react";

import { Button } from "@/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";

import type { GuideGroup, ShiftRole } from "../lib";
import { ShiftGuideCard } from "./ShiftGuideCard";

const ROLE_NOTE: Record<ShiftRole, string> = {
  photo: "Bạn chụp chỉ thấy bài của phòng Photo và bài Chung — bài của phòng khác không hiện ở đây.",
  makeup: "Bạn makeup chỉ thấy bài của phòng Trang điểm và bài Chung — bài của phòng khác không hiện ở đây.",
};

export type ShiftGuideDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Names the shift for the reader: day · time · package · role. */
  subtitle: string;
  role: ShiftRole;
  groups: readonly GuideGroup[];
  groupTitle: (group: GuideGroup) => string;
  /** Guide shown expanded on opening; the first guide when absent. */
  openGuideId?: string | undefined;
};

/** Every guide matched to one shift, grouped concept → package → line (decision 0128). */
export function ShiftGuideDialog({
  open,
  onOpenChange,
  subtitle,
  role,
  groups,
  groupTitle,
  openGuideId,
}: ShiftGuideDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Centred without `translate` on desktop: a translated box becomes the
          containing block of the fixed lightbox inside it, so a zoomed image
          would fill only the dialog, and scroll away with it, not the screen. */}
      <DialogContent className="md:inset-0 md:m-auto md:h-fit md:max-w-2xl md:translate-none">
        <DialogHeader>
          <DialogTitle className="text-base font-bold leading-snug">Hướng dẫn cho ca này</DialogTitle>
          <DialogDescription className="text-xs">{subtitle}</DialogDescription>
        </DialogHeader>
        <GuideGroups
          groups={groups}
          groupTitle={groupTitle}
          firstOpen={openGuideId ?? groups[0]?.guides[0]?.id}
        />
        <p className="text-xs leading-relaxed text-muted-foreground">{ROLE_NOTE[role]}</p>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" className="min-h-11">
              Đóng
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GuideGroups({
  groups,
  groupTitle,
  firstOpen,
}: {
  groups: readonly GuideGroup[];
  groupTitle: (group: GuideGroup) => string;
  firstOpen: string | undefined;
}) {
  // Lives inside DialogContent, which unmounts on close, so every opening
  // starts again from the guide it was opened on.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set(firstOpen ? [firstOpen] : []),
  );
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <div className="min-w-0 space-y-4">
      {groups.map((group) => (
        <div key={`${group.tier}:${group.key}`}>
          <h3 className="mx-0.5 mb-2 text-[11px] font-extrabold uppercase tracking-[0.5px] text-muted-foreground">
            {groupTitle(group)}
          </h3>
          <div className="space-y-2.5">
            {group.guides.map((guide) => (
              <ShiftGuideCard
                key={guide.id}
                guide={guide}
                expanded={expanded.has(guide.id)}
                onToggle={() => toggle(guide.id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
